
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { getOptimizedImageUrl, getThumbnailUrl } from '@/utils/imageUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { useMatureContent } from '@/hooks/use-mature-content';
import { useIsMobile } from '@/hooks/use-mobile';

interface BulletinImageProps {
  src: string;
  thumbnailSrc?: string | null; // Pre-loaded thumbnail for progressive enhancement
  fileType?: string | null;
  isSelected: boolean;
  onClick: () => void;
  onLoad: () => void;
  onError: () => void;
  isMature?: boolean;
  showMatureContent?: boolean; // Keep for backward compatibility but prefer unified hook
  size?: 'small' | 'medium' | 'large' | 'horizontal';
  focalPointX?: number;
  focalPointY?: number;
  fillContainer?: boolean; // New prop for filling container with cover instead of contain
  viewMode?: 'fit-to-width' | 'fit-to-height'; // New prop for lightbox toggle
}

const BulletinImage: React.FC<BulletinImageProps> = ({
  src,
  thumbnailSrc: preloadedThumbnail,
  fileType,
  isSelected,
  onClick,
  onLoad,
  onError,
  isMature = false,
  showMatureContent: propShowMatureContent,
  size = 'medium',
  focalPointX = 50,
  focalPointY = 50,
  fillContainer = false,
  viewMode = 'fit-to-width'
}) => {
  const isMobile = useIsMobile();
  
  // Use the unified mature content hook
  const { showMatureContent: hookShowMatureContent } = useMatureContent();
  
  // Use prop value if provided, otherwise use hook value
  const showMatureContent = propShowMatureContent !== undefined ? propShowMatureContent : hookShowMatureContent;
  
  // Determine if we need to blur the image
  const shouldBlur = isMature && !showMatureContent;
  
  // Generate different quality URLs based on selection state
  // Use preloaded thumbnail if available, otherwise generate one
  const thumbnailSrc = preloadedThumbnail || (src ? getThumbnailUrl(src, 200) : null);
  const optimizedSrc = src ? getOptimizedImageUrl(src) : null;
  
  // State for progressive loading in lightbox
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const [isFullImageLoaded, setIsFullImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  
  // For lightbox (isSelected=true): show thumbnail immediately, load full image in background
  // For grid view: use thumbnail directly
  useEffect(() => {
    if (isSelected && thumbnailSrc && optimizedSrc) {
      // Start with thumbnail immediately (no loading state)
      setCurrentSrc(thumbnailSrc);
      setIsFullImageLoaded(false);
      
      // Load full image in background
      const img = new Image();
      img.src = optimizedSrc;
      img.onload = () => {
        // Smoothly transition to full image when ready
        setCurrentSrc(optimizedSrc);
        setIsFullImageLoaded(true);
      };
      img.onerror = () => {
        console.error(`Failed to load full image: ${optimizedSrc}`);
        // Keep showing thumbnail on error
      };
    } else if (!isSelected && thumbnailSrc) {
      // Grid view: use thumbnail directly
      setCurrentSrc(thumbnailSrc);
      setIsFullImageLoaded(true);
    } else {
      setCurrentSrc(null);
      setIsFullImageLoaded(false);
    }
  }, [isSelected, thumbnailSrc, optimizedSrc]);
  
  const isLoaded = !!currentSrc;
  
  // Debug logging for relic images
  if (src && src.includes('relic-images')) {
    console.log('🖼️ [BULLETIN IMAGE] Relic image debug:', {
      originalSrc: src,
      thumbnailSrc,
      optimizedSrc,
      currentSrc,
      isSelected,
      isLoaded,
      isFullImageLoaded
    });
  }
  
  // Call the onLoad handler when the image is fully loaded
  useEffect(() => {
    if (isLoaded && currentSrc) {
      onLoad();
    }
  }, [isLoaded, onLoad, currentSrc]);

  // Maintain aspect ratio based on size
  const getAspectRatio = () => {
    if (size === 'horizontal') return 'aspect-[16/9]';
    return 'aspect-square'; // Default square aspect ratio
  };
  
  return (
    <div 
      className={cn(
        "relative w-full h-full flex items-center justify-center",
        isSelected ? "overflow-visible" : "overflow-hidden rounded-xl",
        !isSelected && getAspectRatio()
      )}
      onClick={onClick}
      style={{
        height: "100%",
        minHeight: "inherit",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {!currentSrc && !shouldBlur && (
        <Skeleton 
          className={cn(
            "w-full h-full rounded-xl",
            size === 'horizontal' ? 'aspect-[16/9]' : 'aspect-square'
          )} 
        />
      )}
      
      {currentSrc && (
        <img
          ref={imgRef}
          src={currentSrc}
          alt="Bulletin content"
          className={cn(
            "w-full h-full object-cover",
            isSelected 
              ? (viewMode === 'fit-to-height' ? 'rounded-none' : 'rounded-lg') 
              : 'rounded-lg',
            !isSelected && "group-hover:scale-105",
            shouldBlur && "blur-xl",
            // Smooth fade when swapping from thumbnail to full image
            "transition-opacity duration-300 opacity-100"
          )}
          onError={onError}
          style={{
            // Default: 'contain' to show full image. Toggled (fit-to-height): 'cover' to fill viewport immersively
            objectFit: fillContainer 
              ? 'cover' 
              : (isSelected 
                  ? (viewMode === 'fit-to-height' ? 'cover' : 'contain') 
                  : (size === 'horizontal' ? 'contain' : 'cover')),
            objectPosition: `${focalPointX}% ${focalPointY}%`,
            transition: 'opacity 0.3s ease-in-out, transform 0.2s ease-out',
            // When zoomed (fit-to-height), fill the entire viewport
            width: isSelected && viewMode === 'fit-to-height' ? '100%' : (isSelected ? 'auto' : '100%'),
            height: isSelected && viewMode === 'fit-to-height' ? '100vh' : (isSelected ? 'auto' : 'auto'),
            maxWidth: isSelected ? '100vw' : '100%',
            maxHeight: isSelected ? '100vh' : 'none',
            borderRadius: isSelected && viewMode === 'fit-to-height' ? 0 : undefined
          }}
        />
      )}
      
      {/* Mature content warning overlay */}
      {shouldBlur && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white p-2 text-center z-10">
          <div>
            <p className="text-sm font-bold">Mature Content</p>
            <p className="text-xs">Enable mature content in settings to view</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulletinImage;
